import React, { useEffect, useState } from "react";
import InputFieldPassword from "../../../components/Items/PasswordInput";
import { CancelOutlined, CheckCircleOutlineOutlined, LockTwoTone } from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";
import LoadingBar from "react-top-loading-bar";
import * as Yup from "yup";

import ButtonMD from "../../../components/Items/Button";
import CardMD from "../../../components/Items/CardComponent";
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material";
import Form from "../../../components/Form";
import toastAlert from "../../../components/Items/ToastReact";
import url from "../../BaseUrl/url";
import { useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const VerifyAndUpdate = () => {
  const theme = useTheme();

  const [progress, setProgress] = useState(0);

  const colors = tokens(theme.palette.mode);

  const [searchParams] = useSearchParams();

  const userData = searchParams.get("data");

  const [userId, setUserId] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const handleVerifyAndUpdate = async (data, { resetForm }) => {
    setIsUpdate(true);

    try {
      const response = await fetch(`${url}users/sub-admin/verify-and-update-password`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: userId,
          password: data?.password,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toastAlert("success", result?.message || "Account Activated Successfully");

        setTimeout(() => {
          window.location.href = "/";
        }, 200);
      } else {
        toastAlert("error", result.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      toastAlert("error", error?.message || "Network Error");
    } finally {
      setIsUpdate(false);
    }
  };

  const PasswordRequirements = ({ password }) => {
    const requirements = [
      { label: "Password must contain a number", regex: /[0-9]/ },
      { label: "Password must contain a lowercase letter", regex: /[a-z]/ },
      { label: "Password must contain an uppercase letter", regex: /[A-Z]/ },
      { label: "Password must contain a special character", regex: /[^\w]/ },
    ];

    return (
      <Box sx={{ mt: 1 }}>
        {requirements.map((req, index) => (
          <Typography
            key={index}
            sx={{
              color: req.regex.test(password) ? "green" : "gray",
              fontSize: "0.875rem",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              paddingLeft: "5px",
              gap: "5px",
              marginTop: ".5em",
            }}
          >
            {req.regex.test(password) ? <CheckCircleOutlineOutlined /> : <CancelOutlined />}{" "}
            {req.label}
          </Typography>
        ))}
      </Box>
    );
  };

  useEffect(() => {
    if (!userData) {
      handleInvalidToken("Invalid link. Please request a new link");
      return;
    }

    try {
      const token = jwtDecode(userData);

      if (!token?.id || token.exp < Math.floor(Date.now() / 1000)) {
        handleInvalidToken("Invalid link. Please request a new link");
        return;
      }

      setUserId(token.id);
    } catch (error) {
      console.error("Token decoding error:", error);
      handleInvalidToken("Invalid link. Please request a new link");
    }
  }, [userData]);

  const handleInvalidToken = (message) => {
    toastAlert("error", message);
    window.location.href = "/";
  };

  return (
    <div
      style={{
        backgroundColor: `${colors.primary[500]}`,
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Grid container>
        <Grid item xs={12} md={12}>
          <LoadingBar
            color={`${colors.primary[100]}`}
            progress={progress}
            onLoaderFinished={() => setProgress(0)}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            overflow: "hidden",
          }}
          align="center"
        >
          <CardMD
            maxWidth={470}
            borderRadius={5}
            ContentData={
              <>
                <Typography
                  variant="h4"
                  gutterBottom
                  textAlign="center"
                  style={{
                    fontWeight: 500,
                    color: `${colors.primary[100]}`,
                    marginBottom: "20px",
                  }}
                >
                  Create Strong Password <br></br>to Activate Your Account
                </Typography>

                <Form
                  initialValues={{
                    password: "",
                    confirm_password: "",
                  }}
                  validationSchema={Yup.object().shape({
                    password: Yup.string().matches(
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
                    ),
                    confirm_password: Yup.string().oneOf(
                      [Yup.ref("password"), null],
                      "Passwords must match"
                    ),
                  })}
                  onSubmit={handleVerifyAndUpdate}
                >
                  {({ errors, values, touched, handleChange, handleSubmit }) => (
                    <div style={{ marginLeft: "20px", marginRight: "20px" }}>
                      <div style={{ marginBottom: "20px" }}>
                        <InputFieldPassword
                          autoFocus={true}
                          name="password"
                          value={values.password}
                          onChngeterm={handleChange("password")}
                          icon={<LockTwoTone />}
                          type="password"
                          variant="outlined"
                          label="New Password"
                          error={errors.password && touched.password}
                          helperText={
                            ((touched.password && errors.password) || values.password) && (
                              <PasswordRequirements password={values.password} />
                            )
                          }
                        />
                      </div>

                      <div style={{ marginBottom: "10px" }}>
                        <InputFieldPassword
                          name="confirm_password"
                          value={values.confirm_password}
                          onChngeterm={handleChange("confirm_password")}
                          error={touched.confirm_password && Boolean(errors.confirm_password)}
                          helperText={touched.confirm_password && errors.confirm_password}
                          icon={<LockTwoTone />}
                          type="password"
                          variant="outlined"
                          label="Confirm New Password"
                        />
                      </div>
                      <div style={{ marginTop: "5%" }}>
                        <ButtonMD
                          onClickTerm={handleSubmit}
                          title="Update"
                          type="submit"
                          disabled={isUpdate}
                        />
                      </div>
                    </div>
                  )}
                </Form>
              </>
            }
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default VerifyAndUpdate;
